import {makeAutoObservable, reaction} from 'mobx';

import type {UserExpandedCommon} from '../../../api/user';
import type {Destructible, WithReactionDispose} from '../../../utils/store';
import type {TextInputField} from '../../../utils/validation';
import type {OnboardingStep, Tracker, TrackerSlug} from './types';

import {currentUserStore} from '../../../api/user';
import {logger} from '../../../utils/logger';
import {ReactionDisposeManager} from '../../../utils/store';
import {createTrackerTextInputField} from '../utils/createTrackerTextInpurField';
import {FoodDiaryTrackersStore} from './FoodDiaryTrackers.store';

const foodDiaryOnboardingMetadata: UserExpandedCommon['metadata'] = {
  fd_onboarding: {
    is_finished: true,
  },
};

type OnboardingTracker = Tracker & {
  isTrackerSelected: boolean;
  unit: string;
};

export class FoodDiaryOnboardingStore
  implements Destructible, WithReactionDispose
{
  disposer = new ReactionDisposeManager();
  readonly fields = new Map<TrackerSlug, TextInputField>();
  private allTrackers: OnboardingTracker[];
  private step: OnboardingStep = 'introduction-1';
  private trackersStore = new FoodDiaryTrackersStore();

  constructor() {
    makeAutoObservable(this, undefined, {
      autoBind: true,
    });

    this.disposer.watch(
      'check_tracker_store',
      reaction(
        () => this.isLoaded,
        () => {
          this.allTrackers = this.createTrackersList();

          this.generateFields();
        },
      ),
    );
  }

  destruct() {
    this.disposer.dispose();
  }

  get areFieldsValid() {
    return Array.from(this.fields.values()).every(
      field => field.isValid === null || field.isValid,
    );
  }

  get hasSelectedTrackers(): boolean {
    return Boolean(Object.keys(this.selectedTrackersList).length);
  }

  get isLoaded() {
    return this.trackersStore.isLoaded;
  }

  get isOnboardingCompleted(): boolean {
    return !!currentUserStore.user?.metadata?.fd_onboarding?.is_finished;
  }

  get isUpdating() {
    return this.trackersStore.isUpdating;
  }

  get mainTracker() {
    return this.trackersStore.mainTracker;
  }

  get mainTrackerSlug() {
    return this.trackersStore.mainTrackerSlug;
  }

  get needShowMainTracker(): boolean {
    return this.trackersStore.needShowMainTracker;
  }

  get onboardingStep() {
    return this.step;
  }

  get renderTrackerGoals() {
    return this.selectedTrackersList.sort((a, b) => {
      if (this.mainTrackerSlug === a.slug) {
        return -1;
      }

      if (this.mainTrackerSlug === b.slug) {
        return 1;
      }

      return 0;
    });
  }

  get selectedTrackersList(): Tracker[] {
    return this.trackersStore.selectedTrackersList;
  }

  get trackersList() {
    const trackersList: OnboardingTracker[] = [];

    this.allTrackers.forEach(tracker => {
      trackersList.push({
        ...tracker,
        isTrackerSelected: Boolean(
          this.trackersStore.selectedTrackers[tracker.slug],
        ),
      });
    });

    return trackersList;
  }

  getTrackerLabel(slug: TrackerSlug): string {
    return this.trackersStore.getTrackerLabel(slug);
  }

  getTrackerUnit(slug: TrackerSlug): string {
    return this.trackersStore.getTrackerUnit(slug);
  }

  isTrackerPicked(slug: TrackerSlug): boolean {
    const selectedTrackers = this.trackersStore.selectedTrackers;

    return Object.prototype.hasOwnProperty.call(selectedTrackers, slug);
  }

  async onChangeTrackers() {
    if (this.isOnboardingCompleted) {
      await this.trackersStore.updateTrackers();
    } else {
      await this.trackersStore.createTrackers();

      await this.completeOnboarding();
    }
  }

  onStepChange(step: OnboardingStep) {
    this.step = step;
  }

  setMainTrackerSlug(slug: TrackerSlug) {
    this.trackersStore.setMainTrackerSlug(slug);
  }

  setShowMainTracker(isShow: boolean) {
    this.trackersStore.setShowMainTracker(isShow);
  }

  setTrackerGoal(slug: string, amount: string) {
    this.trackersStore.setTrackerGoal(slug, amount);
  }

  setTrackerView(slug: TrackerSlug, isTrackerSelected: boolean): void {
    this.trackersStore.setTracker({isTrackerSelected, slug});

    this.allTrackers.forEach(tracker => {
      if (tracker.slug === slug) {
        tracker.isTrackerSelected = isTrackerSelected;
      }
    });
  }

  private async completeOnboarding() {
    try {
      const metadata = {
        ...currentUserStore.user!.metadata,
        ...foodDiaryOnboardingMetadata,
      };

      await currentUserStore.update({
        b2b: {
          metadata,
        },
        core: {
          metadata,
        },
      });
    } catch (error) {
      logger.error(error);

      throw error();
    }
  }

  private createTrackersList() {
    const selectedTrackers = this.trackersStore.selectedTrackers;

    return this.trackersStore.trackersList.map(tracker => {
      return {
        ...tracker,
        isTrackerSelected: Object.prototype.hasOwnProperty.call(
          selectedTrackers,
          tracker.slug,
        ),
        unit: this.trackersStore.getTrackerUnit(tracker.slug),
      };
    });
  }

  private generateFields() {
    this.allTrackers.forEach(tracker => {
      this.fields.set(tracker.slug, createTrackerTextInputField(tracker));
    });
  }
}
